import React, { useEffect, useMemo, Suspense, useTransition } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Paper,
  Tooltip,
} from "@mui/material";
import { fetchMyPayments, initiatePayment } from "../../contents/redux/actions/paymentActions.js";
import DefaultLoader from "../utilities/loaders/DefaultLoader.js";
import { toast } from "react-toastify";
import styles from "../utilities/cssmodules/RequestAccess.module.css";
import { useNavigate } from "react-router-dom";

const MyPaymentsContent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isPending, startTransition] = useTransition();
  const { myPayments, loading, error } = useSelector((state) => state.payments);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Fetch payments on mount
  useEffect(() => {
    startTransition(() => {
      dispatch(fetchMyPayments()).catch((err) => {
        console.error("Error fetching payments:", err);
      });
    });
  }, [dispatch]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleInitiatePayment = async (paymentDetailId) => {
    try {
      const url = await dispatch(initiatePayment(paymentDetailId));
      if (url) {
        window.location.href = url; // redirect to payment page
      }
    } catch (err) {
      console.error("Payment initiation failed:", err);
    }
  };

  const handleViewReceipt = (payment) => {
    navigate("/paymentreceipt", { state: { payments: payment } });
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRequests = useMemo(() => {
    return (myPayments || []).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [myPayments, page, rowsPerPage]);

  return (
    <div className={styles["component-wrapper"]}>
      <div className={styles["wrapper"]}>
        <Suspense fallback={<DefaultLoader />}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow className="TableHead">
                  <TableCell className="head">Bill Type</TableCell>
                  <TableCell className="head">Amount</TableCell>
                  <TableCell className="head">Payment Date</TableCell>
                  <TableCell className="head">Status</TableCell>
                  <TableCell className="head">Payment ID</TableCell>
                  <TableCell className="head">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginatedRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No payments found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRequests.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>Subscription</TableCell>
                      <TableCell>${payment.fees || "N/A"}</TableCell>
                      <TableCell>
                        {payment.paymentDate
                          ? new Date(payment.paymentDate).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })
                          : "Calculating..."}
                      </TableCell>
                      <TableCell>{payment.statusName || "N/A"}</TableCell>
                      <TableCell>{payment.id || "N/A"}</TableCell>
                      <TableCell>
                        <div className="generic-table-action-container">
                          {/* Show Pay button only if not successful */}
                          {(payment.statusName === "Failed" || payment.statusName === "Approved") && (
                            <Tooltip title="Make payment" arrow placement="bottom">
                              <button
                                className="primary-button"
                                onClick={() => handleInitiatePayment(payment.id)}
                              >
                                Pay bill
                              </button>
                            </Tooltip>
                          )}

                          {/* Show View Receipt button only if payment is done */}
                          {payment.statusName === "Paid" && (
                            <Tooltip title="View your receipt" arrow placement="bottom">
                              <button
                                className="secondary-button"
                                onClick={() => handleViewReceipt(payment)}
                              >
                                View receipt
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={myPayments?.length || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className={styles["table-pagination"]}
            />
          </TableContainer>
        </Suspense>
      </div>
    </div>
  );
};

export default MyPaymentsContent;
